// Model for the visual keyword-expression builder.
//
// A builder expression is an AND-chain of groups, each group an OR-chain of
// terms (a term may be a multi-word phrase): [["reparatur*", "wartung*"],
// ["gebäude"]] ⇔ "(reparatur* OR wartung*) AND gebäude".
//
// Parsing mirrors the server tokenizer
// (tri-server/src/api/controller/tenderExpression.ts): AND/OR (case-
// insensitive) and parentheses are operators, commas are stripped before
// tokenizing, and adjacent words without an operator merge into one phrase
// term. Expressions that don't fit the AND-of-OR-groups shape (e.g.
// "(a AND b) OR c" or un-parenthesized operator mixes) return null so the
// UI can fall back to raw text editing.

export type ExpressionGroups = string[][];

// Same token stream the server produces: '(', ')', 'AND', 'OR', terms.
export function tokenizeExpression(expression: string): string[] {
  const expr = expression.replace(/,/g, "");
  const tokens: string[] = [];
  // Adjacent non-operator words merge into one phrase term.
  let phrase = "";
  for (const match of Array.from(expr.matchAll(/[()]|[^\s()]+/g))) {
    const piece = match[0];
    const up = piece.toUpperCase();
    if (piece === "(" || piece === ")" || up === "AND" || up === "OR") {
      if (phrase) {
        tokens.push(phrase);
        phrase = "";
      }
      tokens.push(piece === "(" || piece === ")" ? piece : up);
    } else {
      phrase = phrase ? `${phrase} ${piece}` : piece;
    }
  }
  if (phrase) tokens.push(phrase);
  return tokens;
}

type Node = { type: "term"; value: string } | { type: "op"; op: "AND" | "OR"; children: Node[] };

// Recursive-descent parse into an AST. Operators at one parenthesis level must
// be homogeneous — mixing AND and OR without parentheses is ambiguous (the
// server passes it through to TED verbatim), so we refuse to model it.
function parseTokens(tokens: string[]): Node | null {
  let pos = 0;

  const parsePrimary = (): Node | null => {
    if (tokens[pos] === "(") {
      pos++;
      const inner = parseChain();
      if (!inner || tokens[pos] !== ")") return null;
      pos++;
      return inner;
    }
    const token = tokens[pos];
    if (token === undefined || token === ")" || token === "AND" || token === "OR") return null;
    pos++;
    return { type: "term", value: token };
  };

  const parseChain = (): Node | null => {
    const first = parsePrimary();
    if (!first) return null;
    const children: Node[] = [first];
    let op: "AND" | "OR" | null = null;
    while (tokens[pos] === "AND" || tokens[pos] === "OR") {
      const current = tokens[pos] as "AND" | "OR";
      if (op && op !== current) return null; // mixed operators at one level
      op = current;
      pos++;
      const next = parsePrimary();
      if (!next) return null;
      children.push(next);
    }
    if (!op) return first;
    return { type: "op", op, children };
  };

  const result = parseChain();
  if (!result || pos !== tokens.length) return null;
  return result;
}

// A node usable inside an OR-group: a term or a (nested) OR of terms.
function collectOrTerms(node: Node): string[] | null {
  if (node.type === "term") return [node.value];
  if (node.op !== "OR") return null;
  const terms: string[] = [];
  for (const child of node.children) {
    const inner = collectOrTerms(child);
    if (!inner) return null;
    terms.push(...inner);
  }
  return terms;
}

function collectGroups(node: Node): ExpressionGroups | null {
  if (node.type === "op" && node.op === "AND") {
    const groups: ExpressionGroups = [];
    for (const child of node.children) {
      const inner = collectGroups(child);
      if (!inner) return null;
      groups.push(...inner);
    }
    return groups;
  }
  const terms = collectOrTerms(node);
  return terms ? [terms] : null;
}

// Parse an expression string into AND-of-OR groups, or null when the
// expression doesn't fit the builder's shape.
export function parseExpression(expression: string): ExpressionGroups | null {
  const tokens = tokenizeExpression(expression);
  if (!tokens.length) return [];
  const ast = parseTokens(tokens);
  if (!ast) return null;
  return collectGroups(ast);
}

// Serialize groups back into an expression string the server tokenizer reads
// the same way. Multi-term groups are parenthesized when combined with AND.
export function serializeExpression(groups: ExpressionGroups): string {
  const parts = groups
    .map((group) => group.map((t) => t.trim()).filter((t) => t.length))
    .filter((group) => group.length);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].join(" OR ");
  return parts
    .map((group) => (group.length > 1 ? `(${group.join(" OR ")})` : group[0]))
    .join(" AND ");
}

// --- Unified search-term model -------------------------------------------
//
// The builder edits keywords and keyword expressions as ONE list of cards
// (each card OR-combined with the others). Storage stays split for
// compatibility — and because the national source issues one feed query per
// keyword, plain terms are better stored as keywords than as an OR
// expression: a card with a single OR condition maps to `keywords`, cards
// with AND conditions (or raw text) map to `keywordExpressions`.

export interface SearchTermFields {
  keywords: string[];
  keywordExpressions: string[];
}

export type ExpressionModel =
  | { kind: "groups"; groups: ExpressionGroups }
  | { kind: "raw"; text: string };

export function fieldsToModels(fields: SearchTermFields): ExpressionModel[] {
  const models: ExpressionModel[] = [];
  const keywords = (fields.keywords || []).map((k) => k.trim()).filter((k) => k.length);
  if (keywords.length) models.push({ kind: "groups", groups: [keywords] });
  for (const expression of fields.keywordExpressions || []) {
    const groups = parseExpression(expression);
    models.push(groups ? { kind: "groups", groups } : { kind: "raw", text: expression });
  }
  return models;
}

export function modelsToFields(models: ExpressionModel[]): SearchTermFields {
  const keywords: string[] = [];
  const keywordExpressions: string[] = [];
  for (const model of models) {
    if (model.kind === "raw") {
      const text = model.text.trim();
      if (text) keywordExpressions.push(text);
      continue;
    }
    const groups = model.groups
      .map((group) => group.map((t) => t.trim()).filter((t) => t.length))
      .filter((group) => group.length);
    if (!groups.length) continue;
    if (groups.length === 1) keywords.push(...groups[0]);
    else keywordExpressions.push(serializeExpression(groups));
  }
  return { keywords: Array.from(new Set(keywords)), keywordExpressions };
}

// Sanitize a term typed into the builder: strip characters the server treats
// as syntax (commas, parentheses). Standalone OR splits the input into
// multiple terms; standalone AND is rejected (must be a separate group).
export function splitTermInput(input: string): { terms: string[]; containsAnd: boolean } {
  const cleaned = input.replace(/[(),]/g, " ");
  const words = cleaned.split(/\s+/).filter((w) => w.length);
  const terms: string[] = [];
  let current: string[] = [];
  let containsAnd = false;
  for (const word of words) {
    const up = word.toUpperCase();
    if (up === "AND") {
      containsAnd = true;
      if (current.length) terms.push(current.join(" "));
      current = [];
      continue;
    }
    if (up === "OR") {
      if (current.length) terms.push(current.join(" "));
      current = [];
      continue;
    }
    current.push(word);
  }
  if (current.length) terms.push(current.join(" "));
  return { terms, containsAnd };
}
